import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Tag, Button, Modal, Form, Input, InputNumber, Alert, Spin, Descriptions, Space, Empty } from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import adminApi from '../../services/tenantService';
import { SubscriptionPlan } from '../../types/tenant';

const AdminPlansPage: React.FC = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [form] = Form.useForm();

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.getPlans();
      setPlans(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const openCreate = () => {
    setEditingPlan(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (plan: SubscriptionPlan) => {
    setEditingPlan(plan);
    form.setFieldsValue({
      name: plan.name,
      slug: plan.slug,
      basePrice: plan.basePrice,
      maxTrucks: plan.maxTrucks,
      maxDrivers: plan.maxDrivers,
      maxDispatchers: plan.maxDispatchers,
      maxApiCallsPerMonth: plan.maxApiCallsPerMonth,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingPlan) {
        await adminApi.updatePlan(editingPlan.id, values);
      } else {
        await adminApi.createPlan(values);
      }
      setModalOpen(false);
      fetchPlans();
    } catch (err) {
      if ((err as { errorFields?: unknown }).errorFields) return; // form validation
      setError((err as Error).message);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;

  return (
    <div>
      {error && <Alert message={error} type="error" showIcon closable style={{ marginBottom: 16 }} />}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>New Plan</Button>
      </div>

      {plans.length === 0 ? (
        <Empty description="No plans yet" />
      ) : (
        <Row gutter={[16, 16]}>
          {plans.map((plan) => (
            <Col xs={24} md={8} key={plan.id}>
              <Card
                title={
                  <Space>
                    <span>{plan.name}</span>
                    <Tag color={plan.isActive ? 'green' : 'default'}>{plan.isActive ? 'Active' : 'Inactive'}</Tag>
                  </Space>
                }
                extra={<Button type="text" aria-label="Edit plan" icon={<EditOutlined />} onClick={() => openEdit(plan)} />}
              >
                <div style={{ fontSize: 28, fontWeight: 700, marginBottom: 16, fontVariantNumeric: 'tabular-nums' }}>
                  ${plan.basePrice}<span style={{ fontSize: 14, fontWeight: 400, color: '#999' }}>/mo</span>
                </div>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Trucks">{plan.maxTrucks ?? 'Unlimited'}</Descriptions.Item>
                  <Descriptions.Item label="Drivers">{plan.maxDrivers ?? 'Unlimited'}</Descriptions.Item>
                  <Descriptions.Item label="Dispatchers">{plan.maxDispatchers ?? 'Unlimited'}</Descriptions.Item>
                  <Descriptions.Item label="API Calls/mo">{plan.maxApiCallsPerMonth?.toLocaleString() ?? 'Unlimited'}</Descriptions.Item>
                </Descriptions>
                <div style={{ marginTop: 12, fontSize: 12, color: '#999' }}>
                  {plan.subscriberCount || 0} subscribers
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Modal
        title={editingPlan ? 'Edit Plan' : 'New Plan'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        okText={editingPlan ? 'Update' : 'Create'}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="Plan Name" rules={[{ required: true, message: 'Name is required' }]}>
            <Input placeholder="e.g., Growth" />
          </Form.Item>
          {!editingPlan && (
            <Form.Item name="slug" label="Slug" rules={[{ required: true, message: 'Slug is required' }]}>
              <Input placeholder="e.g., growth" />
            </Form.Item>
          )}
          <Form.Item name="basePrice" label="Base Price ($/mo)" rules={[{ required: true, message: 'Price is required' }]}>
            <InputNumber style={{ width: '100%' }} min={0} precision={2} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="maxTrucks" label="Max Trucks">
                <InputNumber style={{ width: '100%' }} min={1} placeholder="Unlimited" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="maxDrivers" label="Max Drivers">
                <InputNumber style={{ width: '100%' }} min={1} placeholder="Unlimited" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="maxDispatchers" label="Max Dispatchers">
                <InputNumber style={{ width: '100%' }} min={1} placeholder="Unlimited" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="maxApiCallsPerMonth" label="API Calls/Month">
                <InputNumber style={{ width: '100%' }} min={100} placeholder="Unlimited" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminPlansPage;
